import type { ProductDetailContentType } from '@/types/domains/index';

/**
 * 商品詳細ページの本文データ（商品別 const）を解決する。
 *
 * **静的マップは持たない**（決定#58）。`src/consts/products/<商品コード>.ts` を
 * `import.meta.glob` で拾い、商品コードで引く。ビルド時に解決されるので、
 * 1商品のページでは該当ファイルのチャンクしか落ちてこない（決定#57 の分割の前提）。
 *
 * **見つからなければ `null` を返す。** 呼び出し側が 404 にするか素通しするかを決める。
 * 旧実装（撤去済み）の `defineAsyncComponent` ＋ try/catch は失敗を握りつぶして
 * 黙って空欄になっていたが、その挙動は引き継がない。
 */
const modules = import.meta.glob<{ default: ProductDetailContentType.ProductDetailContent }>(
  '../consts/products/*.ts'
);

/** `../consts/products/501.ts` → `501` */
const toCode = (path: string): string => path.replace(/^.*\/(.+)\.ts$/, '$1');

const byCode: Record<string, () => Promise<{ default: ProductDetailContentType.ProductDetailContent }>> =
  Object.fromEntries(Object.entries(modules).map(([path, loader]) => [toCode(path), loader]));

export const useProductDetailContent = () => {
  const getContent = async (
    code: string
  ): Promise<ProductDetailContentType.ProductDetailContent | null> => {
    const loader = byCode[code];
    if (!loader) return null;

    return (await loader()).default;
  };

  /** const を持つ商品コードの一覧。移植の進捗確認用 */
  const availableCodes = (): string[] => Object.keys(byCode).sort();

  return {
    getContent,
    availableCodes,
  };
};
